/** 所有展示给用户的异常在此转换，不能直接展示浏览器或后端的英文 message。 */
export function toChineseError(error: unknown, fallback = '操作未完成，请重试'): string {
  const value = error as { name?: string; message?: unknown; status?: number } | null;
  const name = value?.name ?? '';
  const message = typeof value?.message === 'string' ? value.message : typeof error === 'string' ? error : '';
  const deviceErrors: Record<string, string> = {
    NotFoundError: '未找到摄像头或麦克风，请检查设备是否连接并可用',
    DevicesNotFoundError: '未找到摄像头或麦克风，请检查设备是否连接并可用',
    NotAllowedError: '设备权限未开启，请允许浏览器使用摄像头和麦克风后重试',
    PermissionDeniedError: '设备权限被拒绝，请在浏览器设置中开启后重试',
    NotReadableError: '摄像头或麦克风无法读取，请关闭占用设备的其他应用后重试',
    TrackStartError: '设备启动失败，请检查设备连接或关闭占用设备的其他应用',
    OverconstrainedError: '当前设备不支持所需采集参数，请更换摄像头或浏览器',
    ConstraintNotSatisfiedError: '当前设备不支持所需采集参数，请更换摄像头或浏览器',
    SecurityError: '浏览器安全设置阻止了设备访问，请使用加密网页并检查权限',
    AbortError: '操作已中止，请重新开始',
    NotSupportedError: '当前浏览器不支持此功能，请更换浏览器后重试',
  };
  if (deviceErrors[name]) return deviceErrors[name];
  const patterns: Array<[RegExp, string]> = [
    [/requested device not found|devices? not found/i, deviceErrors.NotFoundError!],
    [/permission denied|permission dismissed/i, deviceErrors.NotAllowedError!],
    [/could not start video|device.*in use/i, deviceErrors.NotReadableError!],
    [/failed to fetch|networkerror|load failed|network request failed/i, '网络连接失败，请检查网络后重试'],
    [/subject has not enrolled/i, '该人员编号尚未登记人脸，请先登记'],
    [/model changed|dimensions do not match/i, '人脸模型已变更，请重新登记人脸'],
    [/model is not ready|initialize insightface/i, '服务端人脸模型尚未就绪，请联系管理员'],
    [/session.*expired/i, '本次核验已过期，请重新开始核验'],
    [/session.*consumed/i, '本次核验已提交，请重新开始核验'],
    [/session.*not found/i, '核验记录不存在或已过期清理，请重新开始'],
    [/exactly one face/i, '画面中需有且仅有一张清晰人脸，请调整后重试'],
    [/too blurry/i, '人像模糊，请保持稳定并改善光线后重试'],
    [/too dark/i, '光线过暗，请移到明亮处后重试'],
    [/overexposed/i, '画面过曝，请避开强光后重试'],
    [/move closer/i, '人脸距离过远，请靠近摄像头'],
    [/both video and audio/i, '录像必须包含画面和声音，请检查麦克风权限'],
    [/at least .* seconds/i, '录制时长不足，请完整朗读后重新录制'],
    [/no longer than .* seconds/i, '录制时长超出限制，请重新录制'],
    [/size limit|pixel limit/i, '文件大小或画面尺寸超出限制，请重新采集'],
    [/invalid state transition/i, '当前流程状态已变化，请重置后重新开始'],
  ];
  for (const [pattern, text] of patterns) if (pattern.test(message)) return text;
  // 本地定义或接口已经本地化的提示可保留；未知英文一律使用中文兜底。
  if (/[\u3400-\u9fff]/.test(message) && !/[A-Za-z]{3,}/.test(message)) return message;
  const statuses: Record<number, string> = {
    400: '请求内容不正确，请检查后重试', 401: '登录已失效，请重新登录',
    403: '没有执行此操作的权限', 404: '请求的记录或资源不存在，请刷新后重试',
    409: '当前记录状态不允许此操作，请稍后重试或重新开始',
    413: '上传文件过大，请重新录制', 422: '提交内容不符合要求，请检查后重试',
    429: '操作过于频繁，请稍后重试', 500: '服务处理失败，请联系管理员',
    502: '服务暂时无法连接，请稍后重试', 503: '服务尚未就绪，请稍后重试', 504: '服务响应超时，请稍后重试',
  };
  return statuses[value?.status ?? 0] ?? fallback;
}

/** 只提取可供中文映射的详情，绝不把响应对象或校验参数原样输出到页面。 */
export async function responseError(response: Response): Promise<string> {
  let message: unknown = '';
  try {
    const payload = await response.json() as { detail?: unknown };
    if (typeof payload.detail === 'string') message = payload.detail;
  } catch {
    // 网关可能返回英文 HTML；统一按状态码展示中文。
  }
  return toChineseError({ message, status: response.status });
}
